import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Box, Card, CardHeader, Skeleton, Table, TableBody, TableCell, TableRow } from '@mui/material'
import { styled } from '@mui/material/styles'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomIconButton from '@/components/inputs/CustomIconButton'
import CustomCardContent from '@/components/layouts/CustomCardContent'
import { useAlertContext } from '@/contexts/AlertContext'
import EditProductionPlanObligationPeriodDialog from '@/features/portfolioHierarchy/components/EditProductionPlanObligationPeriodDialog'
import { useProductionPlanObligationPeriodQuery } from '@/features/portfolioHierarchy/hooks/useProductionPlanObligationPeriodQuery'
import type { ProductionPlanObligationPeriod } from '@/features/portfolioHierarchy/types'

const StyledTableRow = styled(TableRow)(() => ({
  '.MuiTableCell-root': {
    border: 'none',
  },
}))

type ProductionPlanObligationPeriodCardProps = {
  countryCode: string
  regulationObjectCode: string
}

type ProductionPlanObligationPeriodTableProps = {
  obligationPeriod: ProductionPlanObligationPeriod | null
  onEditClick: (obligationPeriod: ProductionPlanObligationPeriod) => void
}

type EditDialogState = {
  obligationPeriod: ProductionPlanObligationPeriod | null
  open: boolean
}

/**
 * Formats an ISO date string to a localized date-time string with 24-hour format
 */
function formatDateTime(isoString: string | null | undefined): string {
  if (!isoString) return ''
  return DateTime.fromISO(isoString).toLocaleString({
    ...DateTime.DATETIME_MED,
    hour12: false,
  })
}

/**
 * Formats an ISO date string to a localized date-time string with seconds and 24-hour format
 */
function formatDateTimeWithSeconds(isoString: string | null | undefined): string {
  if (!isoString) return ''
  return DateTime.fromISO(isoString).toLocaleString({
    ...DateTime.DATETIME_MED_WITH_SECONDS,
    hour12: false,
  })
}

function ProductionPlanObligationTable({
  obligationPeriod,
  onEditClick,
}: Readonly<ProductionPlanObligationPeriodTableProps>) {
  const { t } = useTranslation()

  return (
    <>
      <CardHeader
        action={
          obligationPeriod && (
            <CustomIconButton
              Icon={EditOutlinedIcon}
              aria-label={t('portfolio_hierarchy.regulation_object.production_plan_obligation.edit_button_label')}
              color={'primary'}
              onClick={() => onEditClick(obligationPeriod)}
            />
          )
        }
        sx={{ paddingX: 0 }}
        title={t('portfolio_hierarchy.regulation_object.production_plan_obligation.title')}
      />
      <Box display="flex">
        <Table
          aria-label={t('portfolio_hierarchy.regulation_object.production_plan_obligation.title')}
          size="small"
          sx={{ width: '100%' }}
        >
          <TableBody>
            <StyledTableRow>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography color="grey.500" variant="caption">
                  {t('portfolio_hierarchy.regulation_object.production_plan_obligation.start_at')}
                </CustomTypography>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography sx={{ lineHeight: 'inherit' }} variant="inherit">
                  {formatDateTime(obligationPeriod?.obligationPeriod?.startAt)}
                </CustomTypography>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography color="grey.500" variant="caption">
                  {t('portfolio_hierarchy.regulation_object.production_plan_obligation.updated_at')}
                </CustomTypography>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography sx={{ lineHeight: 'inherit' }} variant="inherit">
                  {formatDateTimeWithSeconds(obligationPeriod?.updatedAt)}
                </CustomTypography>
              </TableCell>
            </StyledTableRow>
            <StyledTableRow>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography color="grey.500" variant="caption">
                  {t('portfolio_hierarchy.regulation_object.production_plan_obligation.end_at')}
                </CustomTypography>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography sx={{ lineHeight: 'inherit' }} variant="inherit">
                  {formatDateTime(obligationPeriod?.obligationPeriod?.endAt)}
                </CustomTypography>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography color="grey.500" variant="caption">
                  {t('portfolio_hierarchy.regulation_object.production_plan_obligation.production_plan_name')}
                </CustomTypography>
              </TableCell>
              <TableCell sx={{ width: '25%' }}>
                <CustomTypography sx={{ lineHeight: 'inherit' }} variant="inherit">
                  {obligationPeriod?.productionPlanName || '-'}
                </CustomTypography>
              </TableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </Box>
    </>
  )
}

const ProductionPlanObligationPeriodCard = ({
  countryCode,
  regulationObjectCode,
}: ProductionPlanObligationPeriodCardProps) => {
  const { t } = useTranslation()
  const { obligationPeriod, isLoading, error } = useProductionPlanObligationPeriodQuery({
    countryCode,
    regulationObjectCode,
  })
  const { pushAlert } = useAlertContext()

  const [editDialog, setEditDialog] = useState<EditDialogState>({ open: false, obligationPeriod: null })

  useEffect(() => {
    if (error) {
      pushAlert({
        message: t('portfolio_hierarchy.regulation_object.production_plan_obligation.not_found'),
        severity: 'error',
      })
    }
  }, [error, pushAlert, t])

  function handleCloseDialog() {
    setEditDialog({ obligationPeriod: null, open: false })
  }

  function handleEditObligationSuccess() {
    handleCloseDialog()
  }

  function handleOpenDialog(obligationPeriod: ProductionPlanObligationPeriod) {
    setEditDialog({ obligationPeriod: obligationPeriod, open: true })
  }

  return (
    <Card sx={{ marginY: 3 }}>
      {isLoading ? (
        <Skeleton height={120} variant="rectangular" />
      ) : (
        <CustomCardContent>
          <ProductionPlanObligationTable obligationPeriod={obligationPeriod} onEditClick={handleOpenDialog} />
          <EditProductionPlanObligationPeriodDialog
            obligationPeriod={editDialog.obligationPeriod}
            open={editDialog.open}
            onClose={handleCloseDialog}
            onSuccess={handleEditObligationSuccess}
          />
        </CustomCardContent>
      )}
    </Card>
  )
}

export default ProductionPlanObligationPeriodCard
